import styled, { css } from "styled-components";
import React, { useCallback, useState } from "react";

import Box from "@mui/material/Box";
import { withFiltration } from "hocs";
import { Direction } from "@enums/direction";
import { setSort } from "@store/slices/stats";
import { useAppDispatch } from "@hooks/store";
import { statisticCells } from "@constants/table";
import { device } from "@scss/constants/mediaQuery";
import { Paper, Table, TableBody, TableContainer } from "@mui/material";
import { TableFooter, TableHeader, TableRow } from "@components/moleculs";

import type { FC } from "react";
import type { DateRange, Filters, Sort, Statistic } from "@interfaces/stats";

export type StatsTableProps = {
  sort: Sort;
  dateRange?: DateRange;
  statistic: Statistic[];
  filters?: Partial<Filters>;
  isStatisticCollapsed?: boolean;
};

const StatsTable: FC<StatsTableProps> = ({
  statistic,
  sort,
  isStatisticCollapsed,
}) => {
  const dispatch = useAppDispatch();

  const [orderBy, setOrderBy] = useState<keyof Statistic>(sort.orderBy);
  const [direction, setDirection] = useState(sort.direction);
  const [visibleRows, setVisibleRows] = useState<Statistic[]>([]);

  const calcVisibleRows = useCallback((rows: Statistic[]) => {
    setVisibleRows(rows);
  }, []);

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof Statistic,
  ) => {
    const isTheSameCell = orderBy === property;

    const isAsc = direction === Direction.Asc;

    const order = isTheSameCell
      ? isAsc
        ? Direction.Desc
        : Direction.Asc
      : direction;

    setDirection(order);
    setOrderBy(property);

    const sort: Sort = { orderBy: property, direction: order };

    dispatch(setSort(sort));
  };

  return (
    <Wrapper $isStatisticCollapsed={isStatisticCollapsed}>
      <Box sx={{ width: "100%", height: "100%" }} id="statistic-table-box">
        <Paper sx={{ height: "100%" }}>
          <TableContainer
            sx={{
              height: "100%",
              maxWidth: "calc(100vw - 40px)",
            }}
          >
            <Table aria-labelledby="tableTitle" size="medium" stickyHeader>
              <TableHeader
                withSort
                orderBy={orderBy}
                withCheckBox={false}
                sx={{ fontSize: "10pt" }}
                sortDirection={direction}
                headCells={statisticCells}
                onRequestSort={handleRequestSort}
              />

              <TableBody>
                {visibleRows.map((row) => {
                  return <TableRow row={row} key={row.id} />;
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <TableFooter
            order={direction}
            rows={statistic}
            orderBy={orderBy}
            tableData={statistic}
            direction={direction}
            setVisibleRows={calcVisibleRows}
            withSort={false}
          />
        </Paper>
      </Box>
    </Wrapper>
  );
};

const Wrapper = styled.div<{
  $isStatisticCollapsed?: boolean;
}>`
  padding-top: ${({ $isStatisticCollapsed }) =>
    $isStatisticCollapsed ? 0 : 60}px;
  padding-bottom: 30px;

  height: ${({ $isStatisticCollapsed }) => {
    if ($isStatisticCollapsed) {
      return "100%";
    }

    return "600px";
  }};

  ${device.laptopS(css`
    padding-top: 0;
    padding-bottom: 0;
    height: 95%;
  `)}
`;

const WrappedStatsTable = withFiltration(StatsTable);

export default WrappedStatsTable;
